import React, { memo, useCallback, useMemo, useState } from 'react';
import { SearchBar, Text as HoiText } from '@Sage/hoi-poi-ui';

import { UserAvatar } from 'components/avatar';
import Text from 'containers/components/Fields/Text';
import { getLiteral } from 'utils/getLiteral';

import './styles.scss';

const Users = memo(({ data, errors, field, getError, onChange }) => {
    const [filterText, setFilterText] = useState('');

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================
    const handleOnChange = useCallback(
        (id) => {
            return (value) => {
                const newValues = {
                    newValuesDefined: {
                        ...data.valuesDefined,
                        [id]: value,
                    },
                    teamAggregatedUserChange: true,
                };
                onChange && onChange(newValues);
            };
        },
        [data, onChange],
    );

    const handleSearchChange = useCallback((event) => {
        setFilterText(event?.target?.value || '');
    }, []);

    // ========================================
    // RENDER
    // ========================================
    const renderFields = useMemo(() => {
        return field.fields
            .filter((user) => {
                if (!filterText) return true;
                return user.name.toLowerCase().indexOf(filterText.toLowerCase()) > -1;
            })
            .map((current, index) => {
                const classes = ['users-field-list__item'];
                if (index % 2 !== 0) classes.push('users-field-list__item--right');
                else classes.push('users-field-list__item--left');

                const renderLabel = (
                    <div className="users-field-list__item-label">
                        <div className="users-field-list__item-avatar">
                            <UserAvatar id={current.id} />
                        </div>
                        <HoiText className="users-field-list__item-name">
                            {current.name}
                            {current.mandatory && <span className="fm-asterisk-mandatory">*</span>}
                        </HoiText>
                    </div>
                );

                return (
                    <div className={classes.join(' ')} key={current.id}>
                        <Text
                            error={getError(errors[current.id], current.dataLength)}
                            label={renderLabel}
                            labelMode="horizontal"
                            onChange={handleOnChange(current.id)}
                            type="number"
                            value={data?.valuesDefined?.[current.id] || ''}
                        />
                    </div>
                );
            });
    }, [data, errors, field.fields, filterText, getError, handleOnChange]);

    if (!field?.fields || field?.fields.length === 0) return null;

    return (
        <div className="users-field-container">
            <div className="users-field-container__title">
                <HoiText type="subtitle">{getLiteral('label_inidividual_goals_to_reach')}</HoiText>
            </div>
            <div className="users-field-container__search">
                <SearchBar
                    onChange={handleSearchChange}
                    placeholder={getLiteral('cfm_placeholder_search_user')}
                    useAsSimpleSearch
                    value={filterText}
                />
            </div>
            <div className="users-field-container__list">{renderFields}</div>
        </div>
    );
});

Users.displayName = 'Users';

export default Users;
